<script setup lang="ts">
import { computed } from 'vue';

import type { ModuleRecord } from '@/types/domain';
import { LEDGER_COST_COLUMNS, ledgerBucket, ledgerCell, ledgerStep } from '@/utils/change-ledger';

/**
 * 变更台账（0826 Tony：「变更管理里面的样式变成管理台账的格式」——照老 Excel 台账的列）。
 *
 * 只管摆：行数据来自通用列表接口（费用键在后端按权限裁掉），
 * 附件数/申请日期/关闭日期来自 ledger-extras。费用四列整列的显隐由
 * costVisible 定（后端算的：管理员或本项目项目经理）——列藏了、行里的数
 * 也早被后端裁了，前端不是这道门的锁。
 */
const props = defineProps<{
  items: ModuleRecord[];
  /** 按记录 id（字符串键）给的台账扩展列；接口挂了传空对象，那三列显示 — */
  extras: Record<string, {
    attachmentCount: number; appliedAt: string | null; closedAt: string | null;
    stepReady?: boolean;
    /** 库存清点（0827）：数完几行/共几行、合计、其中在客户手上的 */
    stockCounted?: number; stockRows?: number; stockTotal?: number; stockAtCustomer?: number;
  }>;
  /** 费用四列显不显示（后端口径：管理员 / 本项目项目经理） */
  costVisible: boolean;
  /** 模块中心的跨项目台账多一列关联项目 */
  showProject?: boolean;
  /** 项目内部 id → 「项目号 · 名称」 */
  projectLabels?: Record<string, string>;
  selectedId?: number | string | null;
}>();

const emit = defineEmits<{ edit: [record: ModuleRecord] }>();

const rows = computed(() => props.items.map((record, index) => {
  const extra = props.extras[String(record.id)];
  const bucket = ledgerBucket(record.status as string | null);
  return {
    record,
    no: index + 1,
    customerNo: ledgerCell(record, 'customerChangeNo'),
    code: String(record.code ?? '').trim(),
    project: props.projectLabels?.[String(record.project_id ?? '')]
      ?? (record.project_id ? String(record.project_id) : ''),
    source: ledgerCell(record, 'changeSource', 'laiyuan'),
    initiator: ledgerCell(record, 'ownerName', 'owner_name', 'zeren_name'),
    dept: ledgerCell(record, 'initiatedDept'),
    partNumber: ledgerCell(record, 'partNumber', 'pn'),
    title: String(record.title ?? '').trim(),
    files: extra ? extra.attachmentCount : null,
    level: ledgerCell(record, 'changeLevel'),
    costs: LEDGER_COST_COLUMNS.map((column) => ledgerCell(record, column.key)),
    appliedAt: extra?.appliedAt ?? '',
    closedAt: extra?.closedAt ?? '',
    stockCounted: extra?.stockCounted ?? 0,
    stockRows: extra?.stockRows ?? 0,
    stockTotal: extra?.stockTotal ?? 0,
    stockAtCustomer: extra?.stockAtCustomer ?? 0,
    clientAuthorization: ledgerCell(record, 'clientAuthorization'),
    comments: ledgerCell(record, 'comments'),
    bucket,
    // 台账要看得出五步走到哪（0828）；作废/驳回的不显示步
    step: ledgerStep(
      record as { current_step?: unknown; status?: unknown; normalized?: unknown },
      extra?.stepReady === true,
    ),
    status: String(record.status ?? ''),
  };
}));

function dash(value: string | number | null | undefined): string {
  const text = String(value ?? '').trim();
  return text === '' ? '—' : text;
}

/**
 * 库存那一格的悬停说明。
 *
 * 台账上一个孤零零的数字最容易被当成结论。这里把「数了几行、这个数是不是全的」
 * 说清楚——只数了两行的表和数满六行的表，格子里的数字长得一模一样。
 */
function stockTitle(row: { stockRows: number; stockCounted: number; stockTotal: number; stockAtCustomer: number }): string {
  if (!row.stockRows) return '这一单还没走到第 4 步，库存清点表还没铺。';
  if (!row.stockCounted) return '清点表已经铺好，六个位置一个都还没数。';
  const parts = [`${row.stockCounted}/${row.stockRows} 个位置已清点，合计 ${row.stockTotal}`];
  if (row.stockAtCustomer) parts.push(`其中客户处 ${row.stockAtCustomer}（要追回或谈处置）`);
  if (row.stockCounted < row.stockRows) parts.push('还有位置没数，这个合计不是最终数');
  return parts.join('；');
}

/** 长文本列（客户批准/备注/描述）截一手，整段放 title 里悬停看 */
function clip(value: string, max = 18): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}
</script>

<template>
  <div class="ledger-wrap">
    <table class="ledger">
      <thead>
        <tr>
          <th class="num">NO.</th>
          <th>客户 CR 号<small>Customer CR No.</small></th>
          <th>东禾 CR 号<small>ECN No.</small></th>
          <th v-if="props.showProject">关联项目<small>Project</small></th>
          <th>变更来源<small>Source</small></th>
          <th>发起人<small>Initiator</small></th>
          <th>发起部门<small>Function</small></th>
          <th>变更零件号<small>Part Number</small></th>
          <th class="wide">变更描述<small>CR Description</small></th>
          <th>变更文件<small>Files</small></th>
          <th>变更等级<small>Level</small></th>
          <template v-if="props.costVisible">
            <th v-for="column in LEDGER_COST_COLUMNS" :key="column.key" class="cost">
              {{ column.label }}<small>{{ {
                changeExpense: 'Change Expense', clientPayback: 'Change Payback',
                costImpact: 'Cost Impact', priceImpact: 'Price Impact',
              }[column.key] }}</small>
            </th>
          </template>
          <th>申请日期<small>Applied</small></th>
          <th>中止/关闭日期<small>Closed</small></th>
          <th class="wide">客户批准 / 合同<small>Client Approval</small></th>
          <!--
            变更库存（0827 Tony「接到台账上」）：这一单还有多少旧件没处理掉、
            其中多少已经在客户手上。放在状态前面——它和「这单现在到哪了」是一件事。
          -->
          <th class="stock">变更库存<small>Stock on hand</small></th>
          <th>状态<small>五步 / Status</small></th>
          <th>备注<small>Comments</small></th>
          <th class="ops"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="String(row.record.id)"
          :class="{ 'is-selected': props.selectedId !== null && props.selectedId !== undefined
            && String(props.selectedId) === String(row.record.id) }"
          @click="emit('edit', row.record)"
        >
          <td class="num">{{ row.no }}</td>
          <td>{{ dash(row.customerNo) }}</td>
          <td class="code">{{ dash(row.code) }}</td>
          <td v-if="props.showProject">{{ row.project ? row.project : '待归属' }}</td>
          <td>{{ dash(row.source) }}</td>
          <td>{{ dash(row.initiator) }}</td>
          <td>{{ dash(row.dept) }}</td>
          <td class="code">{{ dash(row.partNumber) }}</td>
          <td class="desc" :title="row.title">{{ dash(clip(row.title, 26)) }}</td>
          <td class="num">{{ row.files === null ? '—' : row.files > 0 ? `📎 ${row.files}` : '—' }}</td>
          <td class="num">{{ dash(row.level) }}</td>
          <template v-if="props.costVisible">
            <td v-for="(value, index) in row.costs" :key="LEDGER_COST_COLUMNS[index]!.key" class="cost">
              {{ dash(value) }}
            </td>
          </template>
          <td class="date">{{ dash(row.appliedAt) }}</td>
          <td class="date">{{ dash(row.closedAt) }}</td>
          <td class="desc" :title="row.clientAuthorization">{{ dash(clip(row.clientAuthorization)) }}</td>
          <!--
            没铺过表的单子显示「—」，铺了没数的显示「未清点」——这两件事不一样：
            前者是这单还没走到第 4 步，后者是走到了但没人去数。
            只数了一部分时**必须把分子分母摆出来**，否则台账上读到的是个假总数。
          -->
          <td class="stock" :title="stockTitle(row)">
            <template v-if="!row.stockRows">—</template>
            <template v-else-if="!row.stockCounted"><i class="stock-none">未清点</i></template>
            <template v-else>
              <b>{{ row.stockTotal }}</b>
              <i v-if="row.stockAtCustomer" class="stock-customer">客户处 {{ row.stockAtCustomer }}</i>
              <!-- 台账列已经很挤，只留分子分母；「还没数完」的话在悬停里说 -->
              <i v-if="row.stockCounted < row.stockRows" class="stock-partial">已数 {{ row.stockCounted }}/{{ row.stockRows }}</i>
            </template>
          </td>
          <td>
            <!--
              五步就是状态本身（0828 Tony 定：第1步-申请…第5步-闭环，完成就是关闭）。
              原来「申请蓝/执行琥珀/终止灰/关闭绿」四档折在 status 上，
              和五步各说各话——同是「申请」，第 1 步和第 2 步是两回事。
              现在只摆一格，颜色按步走，越靠后越深。
            -->
            <span
              v-if="row.step"
              class="step-chip"
              :class="`tone-${row.step.tone}`"
              :title="row.status"
            >{{ row.step.label }}</span>
            <!-- 做完了没推进：台账上也要看得出来，否则状态看着「不动」（0828） -->
            <em v-if="row.step?.ready" class="step-ready">待推进</em>
            <span v-else class="step-chip tone-unknown" :title="row.status">—</span>
          </td>
          <td class="desc" :title="row.comments">{{ dash(clip(row.comments, 12)) }}</td>
          <td class="ops">
            <button class="inline-button" type="button" @click.stop="emit('edit', row.record)">编辑 →</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
/* 台账列多，横向滚动放在容器上，页面本体不横滚 */
.ledger-wrap { overflow-x: auto; border: 1px solid var(--line); border-radius: 10px; background: var(--white); }
.ledger { width: 100%; min-width: 1240px; border-collapse: collapse; font-size: 12px; }
.ledger th, .ledger td { padding: 7px 9px; border-bottom: 1px solid var(--line); text-align: left; white-space: nowrap; vertical-align: middle; }
.ledger th { position: sticky; top: 0; background: var(--surface-2, #f7f9fc); font-size: 12px; line-height: 1.3; z-index: 1; }
.ledger th small { display: block; color: var(--muted); font-size: 10px; font-weight: 400; }
/* 库存那一列：数字要能顺着一条线往下扫，附注压小压淡但别糊掉 */
.ledger td.stock { font-variant-numeric: tabular-nums; }
.ledger td.stock b { font-weight: 700; }
.ledger td.stock i { display: inline-block; margin-left: 6px; font-style: normal; font-size: 11px; }
.ledger td.stock .stock-none { color: var(--muted); }
.ledger td.stock .stock-customer { color: #8a5a00; font-weight: 700; }
.ledger td.stock .stock-partial { color: var(--muted); }

/* 费用四列照老台账的黄头，一眼认出这是钱的那一片 */
.ledger th.cost { background: #fdf4d7; }
.ledger td.cost { background: rgba(253, 244, 215, .35); }
.ledger tbody tr { cursor: pointer; }
.ledger tbody tr:hover td { background: rgba(18, 109, 166, .05); }
.ledger tbody tr:hover td.cost { background: rgba(253, 230, 160, .5); }
.ledger tbody tr.is-selected td { background: rgba(18, 109, 166, .08); }
.ledger .num { text-align: center; }
.ledger .code { font-family: var(--mono, ui-monospace, monospace); font-size: 11px; }
.ledger .date { font-variant-numeric: tabular-nums; }
.ledger .desc { max-width: 240px; overflow: hidden; text-overflow: ellipsis; }
.ledger .ops { text-align: right; }
/*
  五步的配色（0828 Tony:「不同颜色标注让状态更加明显」）。
  越靠后越深：申请浅蓝 → 评估青 → 批准紫 → 执行琥珀 → 闭环深绿；
  关闭是实心绿（这单结束了），终止是灰的（它不再往前走）。
*/
.step-chip {
  display: inline-block; padding: 2px 9px; border-radius: 11px;
  font-size: 12px; font-weight: 600; white-space: nowrap; border: 1px solid transparent;
}
.tone-apply   { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.tone-assess  { background: #ecfeff; color: #0e7490; border-color: #a5f3fc; }
.tone-approve { background: #f5f3ff; color: #6d28d9; border-color: #ddd6fe; }
.tone-execute { background: #fffbeb; color: #b45309; border-color: #fde68a; }
.tone-close   { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
.tone-closed  { background: #059669; color: #ffffff; border-color: #059669; }
/* 中途关闭：也是关了，但没走完——琥珀实心，和正常关闭一眼分得开 */
.tone-closed-early { background: #b45309; color: #ffffff; border-color: #b45309; }
.tone-stopped { background: #f1f5f9; color: #64748b; border-color: #e2e8f0; }
.tone-unknown { background: #f8fafc; color: #94a3b8; border-color: #e2e8f0; }
.step-ready { display: block; margin-top: 3px; color: #047857; font-size: 11px; font-style: normal; white-space: nowrap; }

</style>
